import axios from "axios";
import { refreshBrandAccess } from "../auth/refreshBrandAccess";
import { Customer } from "../types/customer";
import { mapConnectedProfiles } from "../utils/connectedProfileMapping";
import { debug, formatSafeErrorMessage } from "../utils/debugLogger";
// Use environment variables with fallbacks for API URLs
const DEV_CUSTOMER_URL = "https://n4kl7g5v9i.execute-api.us-east-1.amazonaws.com/Prod/customers";
const PROD_CUSTOMER_URL = "https://customers.api.sagelyn.ai/customers";

const BASE_URL = process.env.REACT_APP_CUSTOMER_API_URL ||
  (process.env.REACT_APP_ENV === 'development' ? DEV_CUSTOMER_URL : PROD_CUSTOMER_URL);

// Remove duplicate log
debug.log(`Using Customer API URL (${process.env.REACT_APP_ENV}):`, BASE_URL);
// Create Customer API
export const createCustomer = async (data: {
  businessName: string;
  contactEmail: string;
  city: string;
  state: string;
  country: string;
  managerEmail: string;
  gmbAccountId: string;
  gmbLocationId: string;
  agents: { agent_id: string; agent_name: string; link: string }[];
  initialCredits?: number;
}, idToken: string) => {
  debug.log("Sending API request to create a customer...");

  const payload = {
    business_name: data.businessName,
    contact_email: data.contactEmail,
    city: data.city,
    state: data.state,
    country: data.country,
    manager_email: data.managerEmail,
    gmb_account_id: data.gmbAccountId,
    gmb_location_id: data.gmbLocationId,
    agents: data.agents,
    created_by: data.managerEmail,
    status: "ACTIVE",
    initial_credit_balance: data.initialCredits ?? 10,
  };

  try {
    const response = await axios.post(BASE_URL, payload, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
    });
    // The new Brand is only in a newly issued token; get one before any call
    // for it. If this fails the Brand still exists: the error surfaces here
    // rather than as 403s on the next screen.
    await refreshBrandAccess();
    return response.data;
  } catch (error) {
    debug.error("Failed to create customer:", error);
    throw error;
  }
};

// Read All Customers API
export const getAllCustomers = async (idToken: string): Promise<Customer[]> => {
  try {
    const response = await axios.get(BASE_URL, {
      headers: {
        Authorization: `Bearer ${idToken}`, // Add Bearer prefix
        "Content-Type": "application/json",// Include the access token in the Authorization header
      },
    });

    // Map the response to ensure consistent structure
    const customers = response.data as any[];
    return customers.map((customer: any) => ({
      customer_id: customer.customer_id,
      business_name: customer.business_name,
      agency_id: customer.agency_id || undefined,
      contact_email: customer.contact_email,
      city: customer.city || "",
      state: customer.state || "",
      country: customer.country || "",
      timezone: customer.timezone || undefined,
      manager_email: customer.manager_email || "",
      gmb_account_id: customer.gmb_account_id || "",
      gmb_location_id: customer.gmb_location_id || "",
      agents: customer.agents || [],
      connected_profiles: mapConnectedProfiles(customer.connected_profiles) || [],
      created_at: customer.created_at,
      status: customer.status || "ACTIVE",
    })) as Customer[];
  } catch (error) {
    debug.error("Error fetching customers:", error);
    throw error;
  }
};

// Fetch a single customer by ID
export const fetchCustomerById = async (customerId: string, idToken: string): Promise<Customer> => {
  const url = `${BASE_URL}?customer_id=${customerId}`;
  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${idToken}`, // Add Bearer prefix
        "Content-Type": "application/json",
      },
    });
    const customer = response.data as any;
    debug.log("Fetched customer data:", customer);
    return {
      customer_id: customer.customer_id,
      business_name: customer.business_name,
      contact_email: customer.contact_email,
      city: customer.city || "",
      state: customer.state || "",
      country: customer.country || "",
      // The backend always derives/returns timezone via with_timezone().
      // Pass it through so scheduling pickers default to the customer's zone.
      timezone: customer.timezone || undefined,
      manager_email: customer.manager_email || "",
      agents: customer.agents || [],
      connected_profiles: Array.isArray(customer.connected_profiles) ? customer.connected_profiles : [],
      created_at: customer.created_at,
      status: customer.status || "ACTIVE",
    } as Customer;
  } catch (error) {
    debug.error(`Error fetching customer with ID ${customerId}:`, error);
    throw error;
  }
};

export const updateCustomer = async (data: Customer, idToken: string) => {
  if (!idToken)
    alert("Id token is missing");

  try {
    const response = await axios.put(BASE_URL, data, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    debug.error("Error updating customer:", error);
    throw error;
  }
};

/**
 * Creates a customer record during the onboarding process
 * @param data Essential customer data from onboarding
 * @param idToken Authentication token
 * @returns The created customer ID
 */
export const createCustomerFromOnboarding = async (data: {
  business_name: string;
  city: string;
  state: string;
  country: string; // Optional, default to "USA" if not provided
  /** Postal code (SGL-513); customer-management checks the US format. */
  zip_code?: string;
  contact_person_email: string;
  created_by: string  // Add this parameter
}, idToken: string): Promise<string> => {
  debug.log("Creating customer from onboarding data...");

  const payload = {
    business_name: data.business_name,
    contact_email: data.contact_person_email,
    city: data.city,
    state: data.state,
    country: data.country, // Default to USA if not provided
    ...(data.zip_code ? { zip_code: data.zip_code } : {}),
    manager_email: "", // Can be assigned later
    agents: [], // Empty initially
    created_by: data.created_by,
    status: "ONBOARDING",
  };
  debug.log("Final payload being sent:", payload);
  try {
    const response = await axios.post(BASE_URL, payload, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
    });

    // The new Brand is only in a newly issued token (see createCustomer).
    await refreshBrandAccess();
    return (response.data as { customer_id: string }).customer_id;
  } catch (error) {
    debug.error("Failed to create customer during onboarding:", error);
    const err = error as {
      response?: { data?: { error?: string } };
    };
    const apiError = err.response?.data?.error;
    if (typeof apiError === "string" && apiError.trim()) {
      throw new Error(apiError.trim());
    }
    throw new Error(
      formatSafeErrorMessage(error) || "Failed to create customer profile"
    );
  }
};

export type BrandForCustomerResult = {
  customer_id: string;
  owner: { user_id: string; email: string; role: SeatRole; invited: boolean };
  agency_id: string | null;
  credit_balance: number;
  /** "plan" while the Brand is within the customer's paid Brand limit, else "admin". */
  credit_source: "plan" | "admin";
};

/**
 * SuperAdmin "create for customer" (SGL-555): one POST /customers that names the
 * paying customer as the Brand's Owner. The server sets contact_email to theirs,
 * links `agencyId` when given (refused if it is not an Agency), and emails a new
 * login its temporary password. Opening credit is their plan's while this Brand
 * is within their paid Brand limit, else `initialCredits`.
 */
export const createBrandForCustomer = async (data: {
  businessName: string;
  city: string;
  state: string;
  country: string;
  zipCode?: string;
  ownerEmail: string;
  ownerName: string;
  initialCredits: number;
  agencyId?: string;
}, idToken: string): Promise<BrandForCustomerResult> => {
  const payload = {
    business_name: data.businessName,
    city: data.city,
    state: data.state,
    country: data.country,
    ...(data.zipCode ? { zip_code: data.zipCode } : {}),
    owner_email: data.ownerEmail,
    owner_name: data.ownerName,
    contact_email: data.ownerEmail,
    manager_email: "",
    agents: [],
    status: "ONBOARDING",
    initial_credit_balance: data.initialCredits,
    ...(data.agencyId ? { agency_id: data.agencyId } : {}),
  };
  try {
    const response = await axios.post(BASE_URL, payload, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
    });
    return response.data as BrandForCustomerResult;
  } catch (error) {
    debug.error("createBrandForCustomer failed:", formatSafeErrorMessage(error));
    const data = (error as { response?: { data?: { error?: string; customer_id?: string } } }).response?.data;
    const apiError = data?.error;
    // OwnerSeatFailed: the Brand exists without its Owner; the caller lists it
    // so the admin can invite them from the Team card.
    throw Object.assign(
      new Error(typeof apiError === "string" && apiError.trim() ? apiError.trim() : "Couldn't create the Brand."),
      { customerId: data?.customer_id }
    );
  }
};

export const deleteCustomer = async (customerId: string, idToken: string) => {
  const url = `${BASE_URL}?customer_id=${customerId}`;
  try {
    const response = await axios.delete(url, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    debug.error("Error deleting customer:", error);
    throw error;
  }
};

/**
 * Seat role on the Brand (SGL-284 / SGL-390). OWNER controls billing, seats and
 * connections; MEMBER does the work. Only an Owner or SuperAdmin may grant
 * OWNER (server-enforced).
 */
export type SeatRole = "OWNER" | "MEMBER";

/**
 * Cognito group for the login itself — separate from the Seat role. Customers
 * may only create "Customer"; "SuperAdmin" is a Sagelyn staff account and the
 * server refuses it from anyone but a SuperAdmin. "Internal" was retired.
 */
export type InviteAccountType = "Customer" | "SuperAdmin";

/** Body for POST /customers/{customer_id}/invitations. */
export type InviteToCustomerBody = {
  email: string;
  name: string;
  role: SeatRole;
  /** Defaults to "Customer". */
  accountType?: InviteAccountType;
  /**
   * Which allowance the Seat spends (SGL-398). Omitted = BRAND, a teammate on
   * this Brand. AGENCY = an agency operator, counted against the plan's
   * operator seats; the server refuses it on a plan that has none.
   */
  allowance?: "BRAND" | "AGENCY";
};

function inviteToCustomerErrorMessage(error: unknown): string {
  const err = error as {
    response?: {
      status?: number;
      data?: { error?: string; code?: string; detail?: string };
    };
  };
  const status = err.response?.status;
  const data = err.response?.data;
  if (data && (data.error || data.code || data.detail)) {
    const segments: string[] = [];
    if (data.error) segments.push(data.error);
    if (data.code) segments.push(`[${data.code}]`);
    if (data.detail) segments.push(data.detail);
    const msg = segments.join(" ").trim();
    if (msg) {
      return status !== undefined ? `HTTP ${status}: ${msg}` : msg;
    }
  }
  return formatSafeErrorMessage(error);
}

/**
 * POST /customers/{customer_id}/invitations — SuperAdmin, or a Pro Owner into their
 * own Brand (enforced server-side).
 * Success: HTTP 201 (new Cognito user) or 200 (email already in Cognito; assignment applied).
 */
export const inviteUserToCustomer = async (
  customerId: string,
  body: InviteToCustomerBody,
  idToken: string
): Promise<unknown> => {
  const email = body.email?.trim();
  if (!email) {
    throw new Error("Email is required.");
  }
  const name = body.name?.trim();
  if (!name) {
    throw new Error("Name is required.");
  }

  if (body.role !== "OWNER" && body.role !== "MEMBER") {
    throw new Error("Role must be Owner or Team member.");
  }

  const payload: Record<string, string> = {
    email,
    name,
    role: body.role,
    user_type: body.accountType ?? "Customer",
  };
  if (body.allowance) payload.allowance = body.allowance;

  const url = `${BASE_URL}/${customerId}/invitations`;

  try {
    const response = await axios.post(url, payload, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    debug.error("inviteUserToCustomer failed:", formatSafeErrorMessage(error));
    throw new Error(inviteToCustomerErrorMessage(error));
  }
};

/** One person's Seat on a Brand — GET /customers/{customer_id}/seats (SGL-398). */
export type BrandSeat = {
  user_id: string;
  email: string;
  display_name: string;
  role: "OWNER" | "MEMBER";
  allowance: "BRAND" | "AGENCY";
  /** What the Team card shows: the Owner, a teammate on this Brand, or an agency operator. */
  kind: "owner" | "member" | "operator";
  is_you: boolean;
  created_at?: string;
};

export const listBrandSeats = async (customerId: string, idToken: string): Promise<BrandSeat[]> => {
  const response = await axios.get<{ seats: BrandSeat[] }>(`${BASE_URL}/${customerId}/seats`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  return response.data.seats ?? [];
};

/** Owner-only. Removes the Seat; the person keeps their login and other Brands. */
export const removeBrandSeat = async (customerId: string, userId: string, idToken: string): Promise<void> => {
  try {
    await axios.delete(`${BASE_URL}/${customerId}/seats/${encodeURIComponent(userId)}`, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
  } catch (error) {
    throw new Error(inviteToCustomerErrorMessage(error));
  }
};
